import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Loader2, Info, AlertCircle, Brain, AlertTriangle, ShieldCheck,
  CheckCircle2, RefreshCw, X, MessageSquareQuote, FileWarning,
} from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Map COFFITO field labels (UI hint when SRC says "Não coberto")
const _placeholderForField = (label) => `[PROFISSIONAL: complete ${label.toLowerCase()}]`;

const SectionHeader = ({ title, subtitle, icon: Icon, accent = 'amber' }) => (
  <div className="flex items-start gap-2 mb-3" data-testid={`src-section-${title.replace(/\s+/g, '-').toLowerCase()}`}>
    {Icon && <Icon className={`w-5 h-5 mt-0.5 text-${accent}-500 shrink-0`} />}
    <div>
      <h3 className="font-semibold text-sm">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
    </div>
  </div>
);

const ConfidenceBadge = ({ confianca }) => {
  if (confianca === 'EXPLICITO') {
    return (
      <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
        Explícito no relato
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
      Interpretado — confirme
    </Badge>
  );
};

const AchadoCard = ({ achado, index, onChange, readOnly }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-card" data-testid={`src-achado-${index}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <ConfidenceBadge confianca={achado.confianca} />
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="text-xs text-blue-600 dark:text-blue-400 inline-flex items-center gap-1 hover:underline"
          data-testid={`src-achado-${index}-toggle`}
        >
          <MessageSquareQuote className="w-3 h-3" />
          {open ? 'Ocultar' : 'Ver'} trecho do relato
        </button>
      </div>
      <Textarea
        value={achado.achado || ''}
        onChange={(e) => onChange(index, { ...achado, achado: e.target.value })}
        rows={2}
        readOnly={readOnly}
        className="text-sm"
        data-testid={`src-achado-${index}-text`}
      />
      {open && (
        <blockquote className="mt-2 pl-3 border-l-2 border-slate-300 dark:border-slate-600 text-xs italic text-gray-600 dark:text-gray-400">
          💬 {achado.origem_no_relato || '— origem não registrada —'}
        </blockquote>
      )}
    </div>
  );
};

const CampoNaoCoberto = ({ label, value, onChange, readOnly }) => {
  const isMarcado = value && value.startsWith('Não avaliado nesta sessão');
  const handleMarcar = () => {
    const ts = new Date().toLocaleString('pt-BR');
    onChange(`Não avaliado nesta sessão · ${ts}`);
  };
  return (
    <div
      className={`border rounded-lg p-3 flex flex-col gap-2 ${isMarcado ? 'border-slate-300 bg-slate-50 dark:bg-slate-800/40' : 'border-amber-200 dark:border-amber-800/50 bg-amber-50/40 dark:bg-amber-900/10'}`}
      data-testid={`src-campo-${label.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium flex-1">{label}</p>
        {isMarcado && <Badge variant="secondary" className="text-[10px]">Marcado</Badge>}
      </div>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={_placeholderForField(label)}
        readOnly={readOnly}
        className="text-sm"
      />
      {!isMarcado && !readOnly && (
        <Button type="button" variant="outline" size="sm" onClick={handleMarcar} className="w-fit">
          Não avaliado nesta sessão
        </Button>
      )}
    </div>
  );
};

const RedFlagCard = ({ flag, index }) => (
  <div
    className="border-l-4 border-red-500 bg-red-50 dark:bg-red-950/30 rounded-r-lg p-3"
    data-testid={`src-red-flag-${index}`}
  >
    <p className="font-semibold text-red-800 dark:text-red-200 text-sm">{flag.sinal}</p>
    <p className="text-sm text-red-700 dark:text-red-300 mt-1">{flag.alerta}</p>
    <p className="text-xs italic text-red-600 dark:text-red-400 mt-2">
      Identificado em: "{flag.origem_no_relato}"
    </p>
  </div>
);

export const SRCPanel = ({
  consultationId,
  transcription,
  consultationType,
  templateType,
  existingSrcData = null,
  srcConfirmedInitial = false,
  onConfirmed,
  onBypassed,
}) => {
  const { user } = useAuth();
  const [phase, setPhase] = useState(srcConfirmedInitial ? 'confirmed' : (existingSrcData ? 'loaded' : 'loading'));
  const [srcData, setSrcData] = useState(existingSrcData);
  const [confirmedAt] = useState(srcConfirmedInitial ? new Date().toISOString() : null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [edited, setEdited] = useState(false);
  // Editable copies for each section (controlled inputs)
  const [achados, setAchados] = useState([]);
  const [diagnostico, setDiagnostico] = useState({
    funcoes_e_estruturas_corporais: '',
    atividades_e_participacao: '',
    fatores_contextuais: '',
  });
  const [camposNaoCobertosMap, setCamposNaoCobertosMap] = useState({});

  // Professional signature for the disclaimer (computed once per user change)
  const profSignature = useMemo(() => {
    const name = user?.full_name || user?.email || 'profissional';
    const crefito = user?.crefito ? ` · CREFITO ${user.crefito}` : '';
    return `${name}${crefito}`;
  }, [user]);

  // Hydrate editable state from src_data
  const hydrate = useCallback((data) => {
    if (!data) return;
    setAchados(Array.isArray(data.achados_clinicos) ? data.achados_clinicos : []);
    setDiagnostico({
      funcoes_e_estruturas_corporais: data.diagnostico_funcional?.funcoes_e_estruturas_corporais || '',
      atividades_e_participacao: data.diagnostico_funcional?.atividades_e_participacao || '',
      fatores_contextuais: data.diagnostico_funcional?.fatores_contextuais || '',
    });
    const map = {};
    (data.campos_nao_cobertos || []).forEach((label) => {
      // value may already be filled in MongoDB if they pressed "Não avaliado" in a previous session
      map[label] = '';
    });
    setCamposNaoCobertosMap(map);
  }, []);

  const fetchSrc = useCallback(async () => {
    setPhase('loading');
    setError(null);
    try {
      const res = await api.generateSrc(consultationId);
      setSrcData(res.src_data);
      hydrate(res.src_data);
      setPhase('loaded');
    } catch (e) {
      setError(e.response?.data?.detail || 'Não foi possível gerar a Síntese de Raciocínio Clínico.');
      setPhase('error');
    }
  }, [consultationId, hydrate]);

  // Initial trigger: only fetch if no SRC exists yet AND not confirmed
  useEffect(() => {
    if (!existingSrcData && consultationType === 'avaliacao' && !srcConfirmedInitial) {
      fetchSrc();
    } else if (existingSrcData) {
      hydrate(existingSrcData);
    }
  }, [existingSrcData, consultationType, srcConfirmedInitial, fetchSrc, hydrate]);

  const handleAchadoChange = (idx, next) => {
    setAchados((prev) => prev.map((a, i) => (i === idx ? next : a)));
    setEdited(true);
  };

  const handleDiagChange = (key, value) => {
    setDiagnostico((prev) => ({ ...prev, [key]: value }));
    setEdited(true);
  };

  const handleCampoChange = (label, value) => {
    setCamposNaoCobertosMap((prev) => ({ ...prev, [label]: value }));
    setEdited(true);
  };

  const handleConfirm = async () => {
    if (!srcData) return;
    setSubmitting(true);
    try {
      const updatedSrcData = {
        ...srcData,
        achados_clinicos: achados,
        diagnostico_funcional: { ...diagnostico },
        campos_nao_cobertos: srcData.campos_nao_cobertos, // preserve original list of mandatory fields
        red_flags: srcData.red_flags,
        // Persist what professional filled per missing field
        campos_nao_cobertos_preenchidos: camposNaoCobertosMap,
      };
      await api.confirmSrc(consultationId, {
        src_data: updatedSrcData,
        src_confirmed: true,
        src_confirmed_at: new Date().toISOString(),
        src_edited: edited,
        red_flags_count: (srcData.red_flags || []).length,
      });
      setPhase('confirmed');
      toast.success('Síntese confirmada — agora você pode salvar o prontuário.');
      onConfirmed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não foi possível confirmar a Síntese.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismissError = () => {
    setError(null);
    setPhase('error_dismissed');
    toast.info('Síntese ignorada — você pode salvar o prontuário sem ela.');
    onBypassed?.();
  };

  // ---- RENDER ----
  if (consultationType !== 'avaliacao') return null;

  return (
    <Card className="mt-6 border-amber-200 dark:border-amber-800/50" data-testid="src-panel">
      <CardHeader className="bg-gradient-to-r from-amber-50 to-red-50 dark:from-amber-950/30 dark:to-red-950/30 rounded-t-lg">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Brain className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-lg">Síntese de Raciocínio Clínico</CardTitle>
              <CardDescription className="text-xs">
                Estruturado pela IA a partir do seu relato · Revise e confirme antes de salvar
              </CardDescription>
            </div>
          </div>
          <div className="relative group">
            <Info className="w-4 h-4 text-gray-400 cursor-help" />
            <div className="hidden group-hover:block absolute right-0 top-6 w-72 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl z-10">
              Todos os campos abaixo foram gerados a partir do que você verbalizou durante a avaliação.
              A IA organizou e traduziu para linguagem clínica formal — não adicionou informações externas.
              Você é o responsável técnico por este conteúdo.
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-6 space-y-6">
        {/* Loading state */}
        {phase === 'loading' && (
          <div className="flex items-center justify-center gap-3 py-12" data-testid="src-loading">
            <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Estruturando raciocínio clínico…</p>
          </div>
        )}

        {/* Error state */}
        {phase === 'error' && (
          <div className="border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 rounded-lg p-4" data-testid="src-error">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-red-800 dark:text-red-200">Não foi possível gerar a Síntese</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Você pode salvar o prontuário sem ela ou tentar novamente.
                </p>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={fetchSrc} data-testid="src-retry-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Tentar novamente
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleDismissError} data-testid="src-dismiss-btn">
                    <X className="w-4 h-4 mr-2" /> Ignorar e salvar sem Síntese
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmed state — green banner */}
        {phase === 'confirmed' && (
          <div className="border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-4" data-testid="src-confirmed-banner">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-emerald-800 dark:text-emerald-200">Síntese Confirmada</p>
                <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">
                  Confirmada em {new Date(confirmedAt || Date.now()).toLocaleString('pt-BR')}.
                  O prontuário pode ser salvo e exportado.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Loaded / confirmed state — full panel */}
        {(phase === 'loaded' || phase === 'confirmed') && srcData && (
          <>
            {/* === Achados Clínicos === */}
            <section>
              <SectionHeader
                title="Achados Clínicos Identificados"
                subtitle={achados.length === 0 ? 'Nenhum achado foi identificado com clareza. Complete os campos abaixo manualmente.' : `${achados.length} item(ns)`}
                icon={Brain}
              />
              {achados.length === 0 ? (
                <p className="text-sm text-gray-500 italic">Nenhum achado clínico foi identificado com clareza.</p>
              ) : (
                <div className="space-y-2" data-testid="src-achados-list">
                  {achados.map((a, idx) => (
                    <AchadoCard
                      key={`achado-${idx}-${(a.origem_no_relato || a.achado || '').slice(0, 40)}`}
                      index={idx}
                      achado={a}
                      onChange={handleAchadoChange}
                      readOnly={phase === 'confirmed'}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* === Diagnóstico Funcional === */}
            <section>
              <SectionHeader
                title="Diagnóstico Funcional Estruturado"
                subtitle="Organizado conforme CIF · Res. COFFITO 414/2012"
                icon={ShieldCheck}
                accent="emerald"
              />
              <div className="space-y-3" data-testid="src-cif-domains">
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Funções e Estruturas Corporais</label>
                  <Textarea
                    value={diagnostico.funcoes_e_estruturas_corporais}
                    onChange={(e) => handleDiagChange('funcoes_e_estruturas_corporais', e.target.value)}
                    placeholder="[PROFISSIONAL: descrever achados de estruturas e funções corporais avaliadas]"
                    rows={2}
                    readOnly={phase === 'confirmed'}
                    className="mt-1 text-sm"
                    data-testid="src-cif-funcoes"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Atividades e Participação</label>
                  <Textarea
                    value={diagnostico.atividades_e_participacao}
                    onChange={(e) => handleDiagChange('atividades_e_participacao', e.target.value)}
                    placeholder="[PROFISSIONAL: descrever limitações funcionais nas atividades do paciente]"
                    rows={2}
                    readOnly={phase === 'confirmed'}
                    className="mt-1 text-sm"
                    data-testid="src-cif-atividades"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Fatores Contextuais</label>
                  <Textarea
                    value={diagnostico.fatores_contextuais}
                    onChange={(e) => handleDiagChange('fatores_contextuais', e.target.value)}
                    placeholder="[PROFISSIONAL: descrever fatores ambientais e pessoais relevantes]"
                    rows={2}
                    readOnly={phase === 'confirmed'}
                    className="mt-1 text-sm"
                    data-testid="src-cif-fatores"
                  />
                </div>
              </div>
            </section>

            {/* === Campos Não Cobertos === */}
            {srcData.campos_nao_cobertos?.length > 0 && (
              <section>
                <SectionHeader
                  title="⚠️ Campos Não Cobertos no Relato"
                  subtitle="Itens obrigatórios pelo COFFITO 414/2012 não identificados no seu relato. Complete ou marque como não avaliado."
                  icon={FileWarning}
                  accent="amber"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="src-campos-nao-cobertos">
                  {srcData.campos_nao_cobertos.map((label, idx) => (
                    <CampoNaoCoberto
                      key={`${label}-${idx}`}
                      label={label}
                      value={camposNaoCobertosMap[label] || ''}
                      onChange={(v) => handleCampoChange(label, v)}
                      readOnly={phase === 'confirmed'}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* === Red Flags === */}
            {srcData.red_flags?.length > 0 && (
              <section>
                <SectionHeader
                  title="🔴 Alertas Clínicos Identificados"
                  subtitle="Sinais detectados no relato que merecem atenção documental. Não representam diagnóstico."
                  icon={AlertTriangle}
                  accent="red"
                />
                <div className="space-y-2" data-testid="src-red-flags-list">
                  {srcData.red_flags.map((flag, idx) => (
                    <RedFlagCard
                      key={`flag-${idx}-${(flag.sinal || '').slice(0, 40)}`}
                      flag={flag}
                      index={idx}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2 italic">
                  ℹ️ Estes alertas não constituem diagnóstico. São sinais que seu relato contém e que você pode querer documentar explicitamente no prontuário.
                </p>
              </section>
            )}

            {/* === Disclaimer + confirmation === */}
            <section className="pt-4 border-t">
              <div className="bg-slate-100 dark:bg-slate-800/40 rounded-lg p-3 text-xs text-gray-600 dark:text-gray-400 leading-relaxed" data-testid="src-disclaimer">
                Este documento foi gerado com auxílio de inteligência artificial (Akali AI ·{' '}
                <span className="font-mono">{srcData.src_version}</span>) a partir do relato verbal do profissional responsável.
                O conteúdo reflete as informações verbalizadas pelo profissional durante a avaliação,
                organizadas pela IA em linguagem clínica estruturada conforme Res. COFFITO 414/2012.
                A responsabilidade técnica e legal é exclusivamente do profissional que revisou e confirmou este documento.
                <br /><br />
                <strong>Gerado em:</strong> {srcData.gerado_em ? new Date(srcData.gerado_em).toLocaleString('pt-BR') : '—'}
                {phase === 'confirmed' && (
                  <>
                    <br />
                    <strong>Confirmado por:</strong> {profSignature}
                  </>
                )}
              </div>

              {phase !== 'confirmed' && (
                <Button
                  onClick={handleConfirm}
                  disabled={submitting}
                  className="w-full mt-4 bg-gradient-to-r from-amber-500 to-red-500 text-white hover:from-amber-600 hover:to-red-600"
                  size="lg"
                  data-testid="src-confirm-btn"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Confirmando…</> : <>Confirmar Síntese de Raciocínio Clínico ✓</>}
                </Button>
              )}
            </section>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SRCPanel;
